/* camera-lag/timebase.js — what real-world moment each frame of a phone slow-motion clip shows.

   Every latency number of /camera-lag-test is (frames between press and screen change) × (real
   time per frame). Get the second factor wrong and the answer is off ×4 or ×8 with no error
   anywhere, so this decision has ONE owner, with the evidence it used spelled out for the person.

   What phones hand us (measured 2026-10-30 on tests/fixtures/camera-lag, real files):
     · a true high-frame-rate original — iPhone «Slo-mo» kept as shot, Android HSR: the stream
       runs at ~240 fps and its timestamps ARE capture time (jitter 4.07–4.58 ms; an Android file
       had one 17.3 ms hole = 3 frames the camera dropped — trust timestamps, not the index);
     · a RENDERED slow-motion — the iPhone Photos export: every captured frame kept, but re-timed
       (first 1.16 s at 8.33 ms, a ramp, then 33.3 ms = ¼ speed). Its timestamps are playback,
       not capture; the frame INDEX is the clock (802 frames, 782 distinct — all real);
     · a 30 fps file with the capture rate in a tag — Android `com.android.capture.fps`
       (mp4 meta and, after a WebM transcode, Matroska tags): index × 1/tag;
     · a 30 fps file with nothing — a re-encode lost the tag. Only the person knows whether a
       frame is 1/240 s (a «clock from the picture» was measured and dropped: aliasing makes
       60 Hz light at 240 fps identical to 120 Hz at 480 — docs/why/camera-lag-test.md).

   decide(input) → {
     mode: 'timestamps' | 'index' | 'ask',
     captureFps, source: 'stream' | 'tag' | 'ramp' | 'user' | null,
     streamFps,            // what the file's own clock says (median frame gap)
     frameTimesMs,         // Float64Array, presentation order, 0 = first frame (null when 'ask')
     frameMs,              // one frame of real time
     dropped: [{ index, gapMs, missing }],
     ramp: { realFrames, slowFrames } | null,
     candidates: [240, 120, 'normal'] when 'ask'
   }
   input = { ptsUs: number[] (any order), tags: { captureFps?, fullFrameRateIntent? },
             userFps?: number | 'normal' }
*/
(function (root) {
    'use strict';
    if (root.CameraLagTimebase) return;

    var STANDARD = [24, 25, 30, 48, 50, 60, 90, 100, 120, 200, 240, 300, 480, 960];

    function snapFps(f) {
        var best = Math.round(f), bd = Infinity;
        for (var i = 0; i < STANDARD.length; i++) {
            var d = Math.abs(STANDARD[i] - f) / STANDARD[i];
            if (d < bd) { bd = d; best = STANDARD[i]; }
        }
        return bd <= 0.04 ? best : Math.round(f);
    }
    function median(a) {
        if (!a.length) return 0;
        var s = Array.prototype.slice.call(a).sort(function (x, y) { return x - y; });
        var m = s.length >> 1;
        return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    }
    function gapsOf(pts) {
        var g = [];
        for (var i = 1; i < pts.length; i++) g.push((pts[i] - pts[i - 1]) / 1000);
        return g;
    }
    function indexTimes(n, fps) {
        var t = new Float64Array(n), step = 1000 / fps;
        for (var i = 0; i < n; i++) t[i] = i * step;
        return t;
    }
    function stampTimes(pts) {
        var t = new Float64Array(pts.length), t0 = pts[0];
        for (var i = 0; i < pts.length; i++) t[i] = (pts[i] - t0) / 1000;
        return t;
    }
    function droppedOf(gaps, frameMs) {
        var out = [];
        for (var i = 0; i < gaps.length; i++) {
            if (gaps[i] > frameMs * 1.5) out.push({ index: i + 1, gapMs: +gaps[i].toFixed(2), missing: Math.max(1, Math.round(gaps[i] / frameMs) - 1) });
        }
        return out;
    }
    /* A rendered slow-motion: a real-speed stretch (short gaps) AND a slowed stretch
       (gaps ≥ 25 ms) that is not a handful of holes. Returns the capture fps or 0. */
    function rampOf(gaps) {
        var short = [], slow = 0;
        for (var i = 0; i < gaps.length; i++) {
            if (gaps[i] > 0 && gaps[i] < 12) short.push(gaps[i]);
            else if (gaps[i] >= 25) slow++;
        }
        if (short.length >= 20 && slow >= Math.max(20, gaps.length * 0.2)) {
            return { fps: snapFps(1000 / median(short)), realFrames: short.length, slowFrames: slow };
        }
        return null;
    }

    function decide(input) {
        var pts = Array.prototype.slice.call((input && input.ptsUs) || []).filter(function (v) { return isFinite(v); });
        pts.sort(function (a, b) { return a - b; });
        var tags = (input && input.tags) || {};
        var n = pts.length;
        var out = { mode: 'ask', captureFps: null, source: null, streamFps: 0, frameTimesMs: null, frameMs: 0,
                    dropped: [], ramp: null, candidates: null, frames: n };
        if (n < 2) { out.candidates = [240, 120, 'normal']; return out; }
        var gaps = gapsOf(pts);
        var med = median(gaps.filter(function (g) { return g > 0; })) || 1;
        /* The MEAN of the ordinary gaps, not the median: Matroska keeps 1 ms ticks, so a
           240 fps stream is 4,4,4,5,4,… — median 4 ms (250 fps), mean 4.17 ms (240). */
        var ord = gaps.filter(function (g) { return g > med * 0.5 && g < med * 1.5; });
        var mGap = ord.length ? ord.reduce(function (a, b) { return a + b; }, 0) / ord.length : med;
        out.streamFps = +(1000 / mGap).toFixed(2);
        var streamSnap = snapFps(out.streamFps);
        var tagFps = +tags.captureFps > 0 ? +tags.captureFps : 0;
        var user = input && input.userFps;

        // The person's word wins — but «normal» means the file's own clock.
        if (user === 'normal') {
            out.mode = 'timestamps'; out.source = 'user'; out.captureFps = streamSnap;
            out.frameMs = mGap; out.frameTimesMs = stampTimes(pts); out.dropped = droppedOf(gaps, mGap);
            return out;
        }
        if (+user > 0) {
            user = +user;
            if (Math.abs(user - out.streamFps) / user < 0.08) {
                out.mode = 'timestamps'; out.frameTimesMs = stampTimes(pts); out.dropped = droppedOf(gaps, mGap);
                out.frameMs = mGap;
            } else {
                out.mode = 'index'; out.frameTimesMs = indexTimes(n, user); out.frameMs = 1000 / user;
            }
            out.source = 'user'; out.captureFps = user;
            return out;
        }

        var ramp = rampOf(gaps);
        if (ramp) {
            // every captured frame is in the file; the index is the clock
            out.mode = 'index'; out.source = 'ramp'; out.captureFps = ramp.fps;
            out.ramp = { realFrames: ramp.realFrames, slowFrames: ramp.slowFrames };
            out.frameTimesMs = indexTimes(n, ramp.fps); out.frameMs = 1000 / ramp.fps;
            return out;
        }
        if (tagFps >= 100) {
            var tagSnap = snapFps(tagFps);
            if (out.streamFps >= tagSnap * 0.8) {
                out.mode = 'timestamps'; out.source = 'tag'; out.captureFps = tagSnap;
                out.frameMs = 1000 / tagSnap; out.frameTimesMs = stampTimes(pts); out.dropped = droppedOf(gaps, mGap);
            } else {
                out.mode = 'index'; out.source = 'tag'; out.captureFps = tagSnap;
                out.frameTimesMs = indexTimes(n, tagSnap); out.frameMs = 1000 / tagSnap;
            }
            return out;
        }
        if (out.streamFps >= 100) {
            out.mode = 'timestamps'; out.source = 'stream'; out.captureFps = streamSnap;
            out.frameMs = 1000 / streamSnap; out.frameTimesMs = stampTimes(pts); out.dropped = droppedOf(gaps, mGap);
            return out;
        }
        // A ~30/60 fps file with nothing saying what it was shot at.
        out.mode = 'ask';
        out.candidates = [240, 120, 'normal'];
        return out;
    }

    /* How sure a measurement can be at this clock: one frame of uncertainty on each of
       the two ends (press, screen) — reported to the person next to every number. */
    function resolutionMs(tb) { return tb && tb.frameMs ? tb.frameMs : 0; }

    root.CameraLagTimebase = { decide: decide, snapFps: snapFps, resolutionMs: resolutionMs, _median: median };
})(typeof self !== 'undefined' ? self : this);
